import { useEffect, useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead, { SITE_URL } from '../../components/feature/SEOHead';
import { supabase } from '../../lib/supabase';
import type { PatchNote } from '../../types/patchNote';
import { timeAgo } from '../../utils/timeAgo';

type PatchCategory = 'All' | 'Warzone' | 'Multiplayer' | 'Zombies';

const FILTERS: PatchCategory[] = ['All', 'Warzone', 'Multiplayer', 'Zombies'];

const categoryTag: Record<string, string> = {
  Warzone: 'WZ',
  Multiplayer: 'MP',
  Zombies: 'ZM',
};

const categoryAccent: Record<string, string> = {
  Warzone: 'text-[#ff6b35]',
  Multiplayer: 'text-[#00ff41]',
  Zombies: 'text-purple-400',
};

const patchNotesSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Call of Duty Patch Notes — All Updates | CallOfDutyNext.com',
    description:
      'Complete archive of all Call of Duty patch notes. Every balance change, weapon tuning, bug fix, and update for Warzone, Multiplayer, and Zombies — fully documented.',
    url: `${SITE_URL}/patch-notes`,
    isPartOf: { '@type': 'WebSite', name: 'CallOfDutyNext.com', url: SITE_URL },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Patch Notes', item: `${SITE_URL}/patch-notes` },
    ],
  },
];

function Tag({ label }: { label: string }) {
  return (
    <span className="inline-block font-['Barlow_Condensed'] font-bold text-xs tracking-wider border px-2 py-0.5 text-white border-[#3a3a3a] bg-[#2a2a2a]">
      {label}
    </span>
  );
}

function PatchEntry({ patch, defaultOpen }: { patch: PatchNote; defaultOpen: boolean }) {
  const [expanded, setExpanded] = useState(defaultOpen);
  const accent = categoryAccent[patch.category] ?? 'text-gray-300';
  const tag = categoryTag[patch.category] ?? patch.category.toUpperCase();

  return (
    <div className="border-b border-[#1e1e1e] last:border-0">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-left group py-6 px-0 cursor-pointer flex items-start gap-6"
      >
        <div className="shrink-0 w-24 pt-0.5">
          <div className={`font-['Barlow_Condensed'] font-black text-3xl leading-none ${accent}`}>
            {patch.version}
          </div>
        </div>
        <div className="shrink-0 w-px self-stretch bg-[#222]" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-4 mb-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Tag label={tag} />
              <Tag label="PATCH" />
              <span className="text-[#555] text-xs font-['Barlow_Condensed'] font-bold tracking-wider">
                {timeAgo(patch.date)}
              </span>
            </div>
            <div
              className="w-6 h-6 flex items-center justify-center text-[#555] group-hover:text-white transition-all duration-200 shrink-0"
              style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
            >
              <i className="ri-arrow-down-s-line text-lg" />
            </div>
          </div>
          <h3 className="font-['Barlow_Condensed'] font-black text-white text-xl uppercase leading-tight mb-1 group-hover:text-[#00ff41] transition-colors">
            {patch.title}
          </h3>
          <p className="text-[#666] text-sm leading-relaxed">{patch.highlight}</p>
        </div>
      </button>

      {expanded && (
        <div className="pb-8 pl-[7.5rem]">
          <div className="border-t border-[#1e1e1e] pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {patch.sections.map((section) => (
                <div key={section.area}>
                  <h4 className={`font-['Barlow_Condensed'] font-black text-sm uppercase tracking-widest mb-3 ${accent}`}>
                    {section.area}
                  </h4>
                  <ul className="space-y-2.5">
                    {section.changes.map((change, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-[#888] text-sm leading-snug">
                        <span className="mt-1.5 shrink-0 w-1.5 h-1.5 bg-[#444]" />
                        {change}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PatchNotesSkeleton() {
  return (
    <div className="space-y-0">
      {[1, 2, 3].map((i) => (
        <div key={i} className="border-b border-[#1e1e1e] py-6 flex items-start gap-6 animate-pulse">
          <div className="shrink-0 w-24">
            <div className="h-8 w-20 bg-[#1e1e1e] rounded" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-4 w-10 bg-[#1e1e1e] rounded" />
              <div className="h-4 w-14 bg-[#1e1e1e] rounded" />
            </div>
            <div className="h-5 w-3/4 bg-[#1e1e1e] rounded mb-2" />
            <div className="h-4 w-1/2 bg-[#1e1e1e] rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function PatchNotesPage() {
  const [activeFilter, setActiveFilter] = useState<PatchCategory>('All');
  const [patchNotes, setPatchNotes] = useState<PatchNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchPatchNotes = async () => {
      setLoading(true);
      setError(false);
      const { data, error: fetchError } = await supabase
        .from('patch_notes')
        .select('*')
        .eq('published', true)
        .order('date', { ascending: false });

      if (fetchError) {
        setError(true);
      } else {
        setPatchNotes((data as PatchNote[]) ?? []);
      }
      setLoading(false);
    };
    fetchPatchNotes();
  }, []);

  const filtered =
    activeFilter === 'All'
      ? patchNotes
      : patchNotes.filter((p) => p.category === activeFilter);

  const latest = filtered[0];
  const rest = filtered.slice(1);

  const stats = {
    total: patchNotes.length,
    warzone: patchNotes.filter((p) => p.category === 'Warzone').length,
    multiplayer: patchNotes.filter((p) => p.category === 'Multiplayer').length,
    zombies: patchNotes.filter((p) => p.category === 'Zombies').length,
  };

  return (
    <div className="bg-[#111] min-h-screen">
      <SEOHead
        title="Call of Duty Patch Notes — Every Update Documented | CallOfDutyNext.com"
        description="Full archive of Call of Duty patch notes. Every weapon balance change, bug fix, and game update for Warzone, Multiplayer, and Zombies. Fully documented and searchable."
        keywords="Call of Duty patch notes, Warzone update, weapon balance, CoD hotfix, multiplayer patch, zombies update"
        canonical="/patch-notes"
        schema={patchNotesSchema}
      />
      <Navbar />

      <div className="pt-14">
        {/* Page title bar */}
        <div className="border-b border-[#1e1e1e] bg-[#0d0d0d]">
          <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="font-['Barlow_Condensed'] font-black text-white text-3xl uppercase tracking-wider leading-none">
                Patch Notes
              </h1>
              <p className="text-[#555] text-sm mt-1">
                Every balance change, fix, and update — fully documented.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  onClick={() => setActiveFilter(f)}
                  className={`font-['Barlow_Condensed'] font-bold text-xs tracking-widest px-3 py-1.5 border transition-all cursor-pointer whitespace-nowrap ${
                    activeFilter === f
                      ? 'bg-white text-black border-white'
                      : 'border-[#333] text-[#888] hover:border-[#666] hover:text-white'
                  }`}
                >
                  {f === 'All' ? 'ALL' : (categoryTag[f] ?? f.toUpperCase())}
                </button>
              ))}
              {!loading && (
                <span className="text-[#444] text-xs font-['Barlow_Condensed'] ml-2">
                  {filtered.length} {filtered.length === 1 ? 'PATCH' : 'PATCHES'}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-8">
          {error ? (
            <div className="text-center py-24">
              <i className="ri-error-warning-line text-5xl text-[#333] mb-4 block" />
              <p className="font-['Barlow_Condensed'] font-black text-white text-2xl uppercase mb-2">Failed to Load</p>
              <p className="text-[#555] text-sm">Could not fetch patch notes. Please try refreshing.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 lg:gap-10">
              {/* LEFT: Patch entries */}
              <div className="lg:col-span-2 border-r-0 lg:border-r border-[#1e1e1e] pr-0 lg:pr-10">
                {loading ? (
                  <PatchNotesSkeleton />
                ) : filtered.length === 0 ? (
                  <div className="text-center py-24">
                    <i className="ri-file-text-line text-5xl text-[#333] mb-4 block" />
                    <p className="font-['Barlow_Condensed'] font-black text-white text-2xl uppercase mb-2">No Patches Found</p>
                    <p className="text-[#555] text-sm">Try switching the filter above.</p>
                  </div>
                ) : (
                  <>
                    {latest && (
                      <div className="mb-2">
                        <div className="flex items-center gap-3 mb-4">
                          <span className="font-['Barlow_Condensed'] font-black text-xs text-[#555] tracking-widest uppercase">Latest</span>
                          <div className="flex-1 h-px bg-[#1e1e1e]" />
                        </div>
                        <PatchEntry patch={latest} defaultOpen />
                      </div>
                    )}
                    {rest.length > 0 && (
                      <div>
                        <div className="flex items-center gap-3 mb-4 mt-4">
                          <span className="font-['Barlow_Condensed'] font-black text-xs text-[#555] tracking-widest uppercase">Previous</span>
                          <div className="flex-1 h-px bg-[#1e1e1e]" />
                        </div>
                        {rest.map((patch) => (
                          <PatchEntry key={patch.id} patch={patch} defaultOpen={false} />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* RIGHT: Sidebar */}
              <div className="lg:col-span-1 pt-8 lg:pt-0">
                {/* Stats */}
                <div className="border border-[#1e1e1e] bg-[#0d0d0d] p-5 mb-6">
                  <h3 className="font-['Barlow_Condensed'] font-black text-white text-sm uppercase tracking-widest mb-4">
                    Update Stats
                  </h3>
                  {loading ? (
                    <div className="space-y-3 animate-pulse">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="flex justify-between py-2 border-b border-[#1e1e1e] last:border-0">
                          <div className="h-4 w-32 bg-[#1e1e1e] rounded" />
                          <div className="h-6 w-6 bg-[#1e1e1e] rounded" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {[
                        { label: 'Total Patches', value: stats.total, icon: 'ri-file-list-3-line' },
                        { label: 'Warzone Updates', value: stats.warzone, icon: 'ri-map-pin-2-line', color: 'text-[#ff6b35]' },
                        { label: 'Multiplayer Updates', value: stats.multiplayer, icon: 'ri-sword-line', color: 'text-[#00ff41]' },
                        { label: 'Zombies Updates', value: stats.zombies, icon: 'ri-skull-line', color: 'text-purple-400' },
                      ].map((stat) => (
                        <div key={stat.label} className="flex items-center justify-between py-2 border-b border-[#1e1e1e] last:border-0">
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 flex items-center justify-center">
                              <i className={`${stat.icon} text-sm ${stat.color ?? 'text-[#555]'}`} />
                            </div>
                            <span className="text-[#777] text-xs">{stat.label}</span>
                          </div>
                          <span className={`font-['Barlow_Condensed'] font-black text-xl leading-none ${stat.color ?? 'text-white'}`}>
                            {stat.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Most Recent */}
                {!loading && patchNotes[0] && (
                  <div className="border border-[#1e1e1e] bg-[#0d0d0d] p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="font-['Barlow_Condensed'] font-black text-xs text-[#555] tracking-widest uppercase">Most Recent</span>
                    </div>
                    <div className={`font-['Barlow_Condensed'] font-black text-4xl leading-none mb-1 ${categoryAccent[patchNotes[0].category] ?? 'text-white'}`}>
                      {patchNotes[0].version}
                    </div>
                    <p className="text-white font-['Barlow_Condensed'] font-bold text-sm uppercase mb-2">
                      {patchNotes[0].title}
                    </p>
                    <p className="text-[#555] text-xs leading-relaxed mb-3">{patchNotes[0].highlight}</p>
                    <div className="flex items-center gap-2">
                      <Tag label={categoryTag[patchNotes[0].category] ?? 'UPDATE'} />
                      <span className="text-[#555] text-xs font-['Barlow_Condensed'] font-bold">
                        {timeAgo(patchNotes[0].date)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
